import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const EmployeeFilters = ({ 
  searchTerm, 
  onSearchChange, 
  selectedStatus, 
  onStatusChange, 
  selectedHireType,
  onHireTypeChange,
  minSalary,
  onMinSalaryChange,
  maxSalary,
  onMaxSalaryChange,
  selectedMonthYear,
  onMonthYearChange,
  onLastMonthFilter,
  onClearFilters,
  resultsCount
}) => {
  const statuses = [
    { value: '', label: 'Alle Status' },
    { value: 'active', label: 'Aktiv' },
    { value: 'inactive', label: 'Inaktiv' },
    { value: 'suspended', label: 'Gesperrt' }
  ];

  const hireTypes = [
    { value: '', label: 'Alle Anstellungsarten' },
    { value: 'Vollzeit', label: 'Vollzeit' },
    { value: 'Teilzeit', label: 'Teilzeit' },
    { value: 'Mini-job', label: 'Mini-job' }
  ];

  const months = [
    { value: 0, label: 'Januar' },
    { value: 1, label: 'Februar' },
    { value: 2, label: 'März' },
    { value: 3, label: 'April' },
    { value: 4, label: 'Mai' },
    { value: 5, label: 'Juni' },
    { value: 6, label: 'Juli' },
    { value: 7, label: 'August' },
    { value: 8, label: 'September' },
    { value: 9, label: 'Oktober' },
    { value: 10, label: 'November' },
    { value: 11, label: 'Dezember' }
  ];

  // Generate years (current year and 2 years back)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 3 }, (_, i) => currentYear - i);

  const hasActiveFilters = searchTerm || selectedStatus || selectedHireType || minSalary || maxSalary || 
    (selectedMonthYear && (selectedMonthYear.month !== new Date().getMonth() || selectedMonthYear.year !== new Date().getFullYear()));

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6 elevation-1">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 flex-1">
          {/* Search Input */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Icon 
                name="Search" 
                size={20} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" 
              />
              <Input
                type="text"
                placeholder="Mitarbeiter suchen..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e?.target?.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="min-w-36">
            <select
              value={selectedStatus}
              onChange={(e) => onStatusChange(e?.target?.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-input text-text-primary focus:ring-2 focus:ring-primary focus:border-primary"
            >
              {statuses?.map((status) => (
                <option key={status?.value} value={status?.value}>
                  {status?.label}
                </option>
              ))}
            </select>
          </div>

          {/* Hire Type Filter */}
          <div className="min-w-36">
            <select
              value={selectedHireType}
              onChange={(e) => onHireTypeChange(e?.target?.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-input text-text-primary focus:ring-2 focus:ring-primary focus:border-primary"
            >
              {hireTypes?.map((hireType) => (
                <option key={hireType?.value} value={hireType?.value}>
                  {hireType?.label}
                </option>
              ))}
            </select>
          </div>

          {/* Salary Range Filters */}
          <div className="flex gap-2 min-w-48">
            <div className="flex-1">
              <input
                type="number"
                placeholder="Min. Gehalt"
                value={minSalary || ''}
                onChange={(e) => onMinSalaryChange(e?.target?.value ? parseFloat(e.target.value) : '')}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-text-primary focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                min="0"
                step="0.01"
              />
            </div>
            <div className="flex-1">
              <input
                type="number"
                placeholder="Max. Gehalt"
                value={maxSalary || ''}
                onChange={(e) => onMaxSalaryChange(e?.target?.value ? parseFloat(e.target.value) : '')}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-text-primary focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          {/* Month Filter */}
          <div className="flex gap-2 min-w-48">
            <div className="flex-1">
              <select
                value={selectedMonthYear?.month ?? new Date().getMonth()}
                onChange={(e) => onMonthYearChange(parseInt(e.target.value), selectedMonthYear?.year ?? new Date().getFullYear())}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-text-primary focus:ring-2 focus:ring-primary focus:border-primary text-sm"
              >
                {months.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <select
                value={selectedMonthYear?.year ?? new Date().getFullYear()}
                onChange={(e) => onMonthYearChange(selectedMonthYear?.month ?? new Date().getMonth(), parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-text-primary focus:ring-2 focus:ring-primary focus:border-primary text-sm"
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Last Month Quick Filter */}
          <div className="min-w-32">
            <Button
              variant="outline"
              size="sm"
              onClick={onLastMonthFilter}
              iconName="Calendar"
              iconPosition="left"
              className="touch-feedback w-full"
            >
              Letzter Monat
            </Button>
          </div>

        </div>

        {/* Results Count and Clear Filters */}
        <div className="flex items-center gap-4">
          <div className="text-sm text-text-secondary">
            {resultsCount} {resultsCount === 1 ? 'Mitarbeiter' : 'Mitarbeiter'} gefunden
          </div>
          
          {hasActiveFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={onClearFilters}
              iconName="X"
              iconPosition="left"
              className="touch-feedback"
            >
              Filter löschen
            </Button>
          )}
        </div>
      </div>
      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex flex-wrap gap-2">
            <span className="text-sm text-text-secondary">Aktive Filter:</span>
            
            {searchTerm && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary/10 text-primary border border-primary/20">
                Suche: "{searchTerm}"
                <button
                  onClick={() => onSearchChange('')}
                  className="ml-1 hover:text-primary/80"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            
            {selectedStatus && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-accent/10 text-accent border border-accent/20">
                Status: {statuses?.find(s => s?.value === selectedStatus)?.label}
                <button
                  onClick={() => onStatusChange('')}
                  className="ml-1 hover:text-accent/80"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            
            {selectedHireType && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary/10 text-primary border border-primary/20">
                Anstellungsart: {hireTypes?.find(h => h?.value === selectedHireType)?.label}
                <button
                  onClick={() => onHireTypeChange('')}
                  className="ml-1 hover:text-primary/80"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            
            {(minSalary || maxSalary) && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-success/10 text-success border border-success/20">
                Gehalt: {minSalary ? `€${minSalary.toLocaleString('de-DE')}` : '€0'} - {maxSalary ? `€${maxSalary.toLocaleString('de-DE')}` : '∞'}
                <button
                  onClick={() => {
                    onMinSalaryChange('');
                    onMaxSalaryChange('');
                  }}
                  className="ml-1 hover:text-success/80"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            
            {selectedMonthYear && (selectedMonthYear.month !== new Date().getMonth() || selectedMonthYear.year !== new Date().getFullYear()) && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary/10 text-primary border border-primary/20">
                Monat: {months.find(m => m.value === selectedMonthYear.month)?.label} {selectedMonthYear.year}
                <button
                  onClick={() => {
                    const now = new Date();
                    onMonthYearChange(now.getMonth(), now.getFullYear());
                  }}
                  className="ml-1 hover:text-primary/80"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeFilters;